import uniqueId from 'lodash/uniqueId';

import type { ChartFormValues } from '@/features/Dashboard/context/EditChartApp';
import type { BasicTableValues, TileValues } from '@/features/Dashboard/context/EditChartApp/types';
import {
  balancedPalette,
  chartPaletteSelection,
  ColorPalette,
  monoBluePalette,
  vibrantPalette
} from '@/features/Dashboard/form/AppForm/components/ColorPaletteSelector/const';
import type {
  FWDChartWidgetV2,
  MarketApp,
  SeasonalChartWidgetV2,
  SimpleChartWidgetV2,
  TableRow,
  TableWidgetV2,
  TileBigNumber,
  TilesWidgetV2,
  WidgetV2
} from '@/features/Dashboard/types';
import { AppType } from '@/features/Dashboard/types';
import type { FWDTemplateChartSeries, TemplateChartSeries } from '@/types/charts';

const newChart = (values: ChartFormValues) => {
  const palette = chartPaletteSelection.find(c => c.id === values.colorPalette);

  const getColor = (index: number) => {
    const paletteColors = palette?.palette ?? [];
    const paletteIndex = paletteColors.length > 0 ? index % paletteColors.length : 0;
    const colorHex = paletteColors[paletteIndex]?.hex ?? '#000000';
    const colorName = paletteColors[paletteIndex]?.name ?? 'default';
    return { colorHex, colorName };
  };

  let tab;
  if (values.type === AppType.Spot) {
    const series: TemplateChartSeries[] = values.symbols.active.map((symbol, index) => {
      const { colorHex, colorName } = getColor(index);
      return {
        settings: {
          type: 'line',
          color: colorHex,
          colorName
        },
        shortTitle: symbol.shortTitle,
        symbol: {
          key: {
            Code: symbol.symbol.Code,
            PeriodType: symbol.symbol.PeriodType,
            Period: symbol.symbol.Period,
            TimeRef: symbol.symbol.TimeRef
          },
          group: 'Prod_Indexes',
          columns: null
        }
      };
    });

    tab = {
      name: values.name,
      metadata: null,
      series,
      type: 'graph'
    } as SimpleChartWidgetV2;
  } else if (values.type === AppType.Seasonal) {
    const series: TemplateChartSeries[] = values.symbols.active.map((symbol, index) => {
      const { colorHex, colorName } = getColor(index);
      return {
        settings: {
          type: 'line',
          color: colorHex,
          colorName
        },
        shortTitle: symbol.shortTitle,
        symbol: {
          key: {
            Code: symbol.symbol.Code,
            PeriodType: symbol.symbol.PeriodType,
            Period: symbol.symbol.Period,
            TimeRef: symbol.symbol.TimeRef
          },
          group: 'Prod_Indexes',
          columns: null
        }
      };
    });

    tab = {
      name: values.name,
      metadata: {
        graphType: 'seasonal',
        from: values.from ? `${values.from}y` : '2y'
      },
      series,
      type: 'graph'
    } as SeasonalChartWidgetV2;
  } else {
    const series: FWDTemplateChartSeries[] = values.symbols.active.map((symbol, index) => {
      const { colorHex, colorName } = getColor(index);
      return {
        settings: {
          type: 'line',
          color: colorHex,
          colorName
        },
        shortTitle: symbol.shortTitle,
        symbol: {
          key: {
            Code: symbol.symbol.Code,
            PeriodType: symbol.symbol.PeriodType,
            TimeRef: symbol.symbol.TimeRef
          },
          group: 'Prod_Indexes',
          columns: null
        }
      };
    });

    tab = {
      name: values.name,
      metadata: {
        graphType: 'forwardSimple'
      },
      series,
      type: 'graph'
    } as FWDChartWidgetV2;
  }

  return {
    id: uniqueId(new Date().toISOString()),
    tabs: [tab]
  };
};

const newTable = (values: any): MarketApp => {
  const { symbols, name } = values;
  const rows = symbols.active.map((s: any) => {
    return {
      shortTitle: s.shortTitle,
      symbol: { key: { ...s.symbol }, group: s.group },
      diffSymbol: {
        group: s.group,
        key: {
          Code: '',
          PeriodType: '',
          Period: '',
          TimeRef: ''
        }
      }
    };
  });
  return {
    id: uniqueId(new Date().toISOString()),
    tabs: [
      {
        name,
        layout: null,
        metadata: null,
        type: 'table',
        sections: [
          {
            name: '',
            rows
          }
        ]
      } as TableWidgetV2
    ]
  };
};
const newHighlightedPrice = ({ symbols }: TileValues): MarketApp => {
  return {
    id: 'tiles',
    name: '',
    metadata: null,
    type: 'tiles',
    tiles: symbols.active.map(symbol => {
      return {
        id: uniqueId(new Date().toISOString()),
        shortTitle: symbol.shortTitle,
        symbol: {
          key: symbol.symbol,
          group: 'Prod_Indexes'
        }
      };
    })
  };
};
const appFormAdapter = {
  // domain -> form
  toFormValues: (domain: WidgetV2 | TilesWidgetV2) => {
    // Helper: build TabSymbol id from key
    const buildId = (key: any): string =>
      `${key?.Code ?? ''}-${key?.PeriodType ?? ''}-${key?.Period ?? ''}-${key?.TimeRef ?? ''}`;

    // Helper: map series to symbols.active
    const seriesToSymbols = (
      series: TemplateChartSeries[] | FWDTemplateChartSeries[] | TileBigNumber[] | TableRow[] = []
    ) => {
      return series.map(s => ({
        id: buildId(s.symbol?.key),
        shortTitle: s.shortTitle ?? s.symbol?.key?.Code ?? '',
        symbol: {
          Code: (s.symbol as any)?.key?.Code ?? '',
          PeriodType: (s.symbol as any)?.key?.PeriodType ?? '',
          Period: (s.symbol as any)?.key?.Period ?? '',
          TimeRef: (s.symbol as any)?.key?.TimeRef ?? ''
        },
        group: 'Prod_Indexes' as const
      }));
    };
    const findColorPalette = (series: (TemplateChartSeries | FWDTemplateChartSeries)[]) => {
      const color = series[0]?.settings?.color;

      switch (color) {
        case vibrantPalette[0].hex:
          return ColorPalette.Vibrant;
        case monoBluePalette[0].hex:
          return ColorPalette.Monochrome;
        case balancedPalette[0].hex:
          return ColorPalette.Balanced;
        default:
          return ColorPalette.Vibrant;
      }
    };

    if (domain.type === 'tiles') {
      return {
        type: AppType.Tiles,
        symbols: {
          active: seriesToSymbols(domain.tiles),
          backlog: []
        }
      } as TileValues;
    }

    switch (domain.type) {
      case 'graph': {
        const graphWidget = domain;
        let type: AppType;
        if (graphWidget.metadata?.graphType === 'seasonal') {
          type = AppType.Seasonal;
        } else if (graphWidget.metadata?.graphType === 'forwardSimple') {
          type = AppType.FWD;
        } else {
          type = AppType.Spot;
        }

        const series = graphWidget.series ?? [];

        return {
          name: graphWidget.name,
          type,
          symbols: { active: seriesToSymbols(series), backlog: [] },
          colorPalette: findColorPalette(series),
          // from: type === AppType.Seasonal ? graphWidget.metadata?.from : undefined
          from: '2y'
        } as ChartFormValues;
      }
      case 'table': {
        const tableWidget = domain;
        return {
          type: AppType.Table,
          name: tableWidget.name,
          symbols: {
            active: seriesToSymbols(tableWidget.sections.flatMap(s => s.rows)),
            backlog: []
          }
        } as BasicTableValues;
      }
      default:
        throw new Error(`Unknown widget type: ${domain.type}`);
    }
  },

  // form -> domain (original is optional to preserve immutable fields like id)

  newApp: ({
    values
  }: {
    values: ChartFormValues | BasicTableValues | TileValues;
  }): MarketApp | undefined => {
    switch (values.type) {
      case AppType.Table:
        return newTable(values);
      case AppType.Tiles:
        return newHighlightedPrice(values);
      case AppType.Seasonal:
      case AppType.FWD:
      case AppType.Spot:
        return newChart(values);
      default:
        return undefined;
    }
  }
};

export default appFormAdapter;
